import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import SearchResultComponent from 'components/SearchResultComponent';
import UpsertSelfServiceContainerForm from 'components/settings/service-container/UpsertSelfServiceContainerForm';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, Grid, Icon, Loader, Modal } from 'semantic-ui-react';
import { findContainerById, getContainersOverview, searchContainers } from 'services/selfServiceContainerServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import {
  SelfServiceContainerOverviewDto,
  SelfServiceContainerSearchRequest,
  UpsertSelfServiceContainerDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const SelfServiceContainerSegment = styled(PraxStyledSegment)`

  .container-type .label {
    display: inline-block;
    padding-right: 0.5rem;
  }

`

const initialSelfServiceContainerSearch: Partial<SelfServiceContainerSearchRequest> = {
  includesInactive: false
}

interface SelfServiceContainerFieldProps extends FormState {
  values: Partial<SelfServiceContainerSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  hasError: boolean;
  selfServiceContainersLoaded: boolean;
  foundContainers: SelfServiceContainerOverviewDto[];
  container?: UpsertSelfServiceContainerDto;
  containerId?: string;
  searchValues: Partial<SelfServiceContainerSearchRequest>,
  addEditModalOpen: boolean;
  loading: boolean;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
}

class SelfServiceContainersView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      hasError: false,
      foundContainers: [],
      selfServiceContainersLoaded: false,
      container: undefined,
      containerId: undefined,
      searchValues: initialSelfServiceContainerSearch,
      addEditModalOpen: false,
      loading: false,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    getContainersOverview(mandateId, this.state.cancelTokenSource)
        .then(foundContainers => this.setState({foundContainers}))
        .catch(error => {
          this.setState({hasError: true});
          this.handleError(error);
        })
        .finally(() => this.setState({selfServiceContainersLoaded: true}));
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by User');
  }

  openAddEditModal = (id: string | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findContainerById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: string, container?: UpsertSelfServiceContainerDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        container: container,
        containerId: id
      };
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      container: undefined,
      containerId: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    const onFinally = () => {
      this.setState({
        selfServiceContainersLoaded: true
      })
    };

    searchValues.mandateId = mandateId;

    searchContainers(searchValues, cancelTokenSource)
        .then(response => {
              this.setState({
                foundContainers: response
              })
            }
        )
        .catch(noop)
        .finally(onFinally)
    this.hideModal();

  }

  handleError(error: any) {
    const {getMessage} = this.props;
    this.setState({errorMessage: getMessage(error.message)});
  };

  handleChange = debounce(({values}: SelfServiceContainerFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;
    this.setState({selfServiceContainersLoaded: false});

    const onFinaly = () => {
      this.setState({
        selfServiceContainersLoaded: true
      })
    }

    values.mandateId = mandateId;

    searchContainers(values, cancelTokenSource)
        .then(response => {
              this.setState({
                foundContainers: response,
                searchValues: values
              })
            }
        )
        .catch(noop)
        .finally(onFinaly)
  }, 300);

  render(): React.ReactNode {
    const {selfServiceContainersLoaded} = this.state;

    return (
        <SelfServiceContainerSegment>
          <div>
            {this.renderSearchSelfServiceContainerSegment()}
          </div>
          <StyledViewTableRow>
            {!selfServiceContainersLoaded ? this.renderLoader() : this.renderSelfServiceContainersSegment()}
          </StyledViewTableRow>

          {this.renderUpsertSelfServiceContainerForm()}
        </SelfServiceContainerSegment>
    );
  }

  renderSearchSelfServiceContainerSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includesInactive: false
            }}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    )
  }

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundContainers} = this.state;

    const numResults = foundContainers.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign="middle">
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  }

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("selfServiceContainer.active");
    }
    return getMessage("selfServiceContainer.inactive");
  };

  renderSelfServiceContainersSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundContainers} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundContainers.length}
            rowGetter={this.containerRowGetter}
            rowRenderer={this.containerRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 500,
                label: (getMessage("container.id")),
                dataKey: "id",
                cellRenderer: (this.idCellRenderer)
              },
              {
                width: 500,
                label: (getMessage("container.description")),
                dataKey: "description"
              },
              {
                width: 200,
                label: (getMessage("container.type")),
                dataKey: "type",
                cellRenderer: this.typeCellRenderer
              },
              {
                width: 600,
                label: (getMessage("container.selfOnboardEnabled")),
                dataKey: "selfOnboardEnabled",
                cellRenderer: (this.checkCellRenderer)
              },
              {
                width: 600,
                label: (getMessage("container.testSchedulingEnabled")),
                dataKey: "testSchedulingEnabled",
                cellRenderer: (this.checkCellRenderer)
              },
              {
                width: 600,
                label: (getMessage("container.callCenterSupported")),
                dataKey: "callCenterSupported",
                cellRenderer: (this.checkCellRenderer)
              },
              {
                width: 150,
                flexGrow: 1,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    )
  }

  containerRowGetter = ({index}: any) => {
    const {foundContainers} = this.state;

    Object.assign(foundContainers[index], {index: index + 1});

    return foundContainers[index]
  };

  containerRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundContainers} = this.state;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundContainers[index].id)}

        >
          {columns}
        </div>
    );
  };

  idCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;

    let numberOfCompaniesIcon;

    if (rowData.numberOfCompanies === 0) {
      numberOfCompaniesIcon = <Icon name={'warning sign'} color={'red'} title={getMessage("selfServiceContainer.noCompany")}/>
    } else if (rowData.numberOfCompanies > 1) {
      numberOfCompaniesIcon = <Icon name={'warning sign'} color={'yellow'} title={getMessage("selfServiceContainer.moreThenOneCompany")}/>
    }

    let notActiveContainerIcon = !rowData.active
    ? <Icon name={'warning sign'} color={'yellow'} title={getMessage("selfServiceContainer.containerNotActive")}/>
    : "";

    return (
        <React.Fragment>
          <span>
            <div> {rowData.id} {numberOfCompaniesIcon} {notActiveContainerIcon}</div>
          </span>
        </React.Fragment>
    );
  };

  typeCellRenderer = ({cellData, rowData}: any) => {

    const {getMessage} = this.props;
    const paymentsSupported = rowData && rowData.paymentsSupported;

    return (
        <div className="container-type">
          <span className="label">{cellData}</span>
          {paymentsSupported && <Icon name="credit card" size="small" title={getMessage("container.paymentsSupported")}/>}
        </div>
    );
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
        </React.Fragment>
    );
  };

  checkCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("selfServiceContainer.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertSelfServiceContainerForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, container, containerId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("selfServiceContainer")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertSelfServiceContainerForm
                container={container}
                containerId={containerId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withMessages(withAuthContext(SelfServiceContainersView));
